/*
  Elite Fanstore - page collection (assets/elite-collection.js), chargé par sections/main-collection-product-grid.liquid
  quand « Style refonte » est coché (réglages « Défilement infini » et « Recherche dans les filtres »).
  Source : Collections/elite-collection.js ; envoi : Connection/deploy-collection-refonte.mjs.

  1. Défilement infini : la pagination du thème reste dans la page (liens lus par Google, page sans JavaScript) mais
     elle est masquée ; les pages suivantes sont ajoutées à la grille en approchant du bas (rendu de la seule section).
     Après N chargements automatiques, un bouton « Voir plus de produits » (vrai lien vers la page suivante) reprend
     la main, pour que le texte SEO et le pied de page restent atteignables. Retour depuis une fiche produit : les pages
     déjà chargées et la position sont rétablies.
  2. Recherche dans les filtres : champ « Rechercher une équipe » en tête des listes longues (ordinateur et mobile).
     Shopify n'affiche que 100 valeurs par filtre : sur les collections qui en ont plus, la recherche propose aussi les
     équipes manquantes, lues dans assets/elite-filtres-equipes.json (généré par Connection/deploy-filtres-equipes.mjs).
     Un filtre qui n'a qu'un seul choix possible est masqué (ex. « Club ou sélection » sur la page Clubs).
     Une équipe choisie hors des 100 valeurs n'a pas de pastille « Équipe : … ✕ » côté Shopify : elle est ajoutée.
     Type de maillot, Version, Saison : valeurs remises dans l'ordre de Connection/deploy-filtres-equipes.mjs (clé « o » du JSON).
  facets.js remplace #ProductGridContainer et le contenu des filtres à chaque changement : un MutationObserver
  réinstalle tout.
*/
(() => {
  const cfg = Object.assign({ infini: true, auto: 4, recherche: true, rechercheMin: 8, section: null, equipes: null }, window.EliteCollection || {});
  const LANG = (document.documentElement.lang || 'fr').toLowerCase();
  const TEXTES = {
    fr: { plus: 'Voir plus de produits', chargement: 'Chargement…', equipe: 'Rechercher une équipe', chercher: 'Rechercher', aucun: 'Aucun résultat', precedents: 'Voir les produits précédents', autres: 'Autres équipes' },
    en: { plus: 'Show more products', chargement: 'Loading…', equipe: 'Search for a team', chercher: 'Search', aucun: 'No results', precedents: 'Show previous products', autres: 'More teams' },
    es: { plus: 'Ver más productos', chargement: 'Cargando…', equipe: 'Buscar un equipo', chercher: 'Buscar', aucun: 'Sin resultados', precedents: 'Ver los productos anteriores', autres: 'Otros equipos' },
    de: { plus: 'Mehr Produkte anzeigen', chargement: 'Wird geladen…', equipe: 'Team suchen', chercher: 'Suchen', aucun: 'Keine Ergebnisse', precedents: 'Vorherige Produkte anzeigen', autres: 'Weitere Teams' },
    it: { plus: 'Mostra altri prodotti', chargement: 'Caricamento…', equipe: 'Cerca una squadra', chercher: 'Cerca', aucun: 'Nessun risultato', precedents: 'Mostra i prodotti precedenti', autres: 'Altre squadre' },
    pt: { plus: 'Ver mais produtos', chargement: 'A carregar…', equipe: 'Procurar uma equipa', chercher: 'Procurar', aucun: 'Sem resultados', precedents: 'Ver os produtos anteriores', autres: 'Outras equipas' },
    sv: { plus: 'Visa fler produkter', chargement: 'Laddar…', equipe: 'Sök ett lag', chercher: 'Sök', aucun: 'Inga resultat', precedents: 'Visa föregående produkter', autres: 'Fler lag' },
  };
  const T = TEXTES[LANG.slice(0, 2)] || TEXTES.fr;
  const PARAM_EQUIPE = 'filter.p.m.custom.equipe';
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const container = document.getElementById('ProductGridContainer');
  if (!container) return;

  /* ------------------------------------------------------------------ défilement infini */
  const cleEtat = () => `ec-infini:${location.pathname}${location.search.replace(/[?&]page=\d+/, '')}`;
  const lireEtat = () => { try { return JSON.parse(sessionStorage.getItem(cleEtat()) || 'null'); } catch { return null; } };
  const ecrireEtat = (v) => { try { sessionStorage.setItem(cleEtat(), JSON.stringify(v)); } catch { /* navigation privée */ } };
  const navigation = performance.getEntriesByType?.('navigation')[0]?.type;
  let restauration = navigation === 'back_forward' ? lireEtat() : null;
  if (restauration && 'scrollRestoration' in history) history.scrollRestoration = 'manual';

  const infini = {
    installe() {
      const grid = document.getElementById('product-grid');
      const pag = container.querySelector('.pagination-wrapper');
      if (!cfg.infini || !grid || grid.tagName !== 'UL' || !pag || pag.dataset.ecInfini) return;
      pag.dataset.ecInfini = '1';
      const courant = parseInt(pag.querySelector('.pagination__item--current')?.textContent, 10) || 1;
      const numeros = [...pag.querySelectorAll('.pagination__item')].map((a) => parseInt(a.textContent, 10)).filter((n) => n > 0);
      this.grid = grid;
      this.premiere = courant;
      this.derniere = courant;
      this.total = Math.max(courant, ...numeros);
      this.auto = cfg.auto;
      this.vus = new Set([...grid.children].map((li) => li.querySelector('a[href]')?.pathname).filter(Boolean));
      pag.classList.add('ec-pag-cachee');
      if (courant > 1) {
        const haut = document.createElement('div');
        haut.className = 'ec-more ec-more--haut';
        const u = new URL(location.href); u.searchParams.delete('page');
        haut.innerHTML = `<a class="ec-more__btn ec-more__btn--ghost" href="${u.pathname}${u.search}"></a>`;
        haut.firstChild.textContent = T.precedents;
        grid.before(haut);
      }
      if (this.derniere >= this.total) return;
      this.bloc = document.createElement('div');
      this.bloc.className = 'ec-more';
      this.bloc.innerHTML = '<a class="ec-more__btn"></a><span class="ec-more__spin" aria-hidden="true"></span><p class="ec-more__statut visually-hidden" role="status" aria-live="polite"></p>';
      this.bouton = this.bloc.querySelector('.ec-more__btn');
      this.bouton.textContent = T.plus;
      this.bouton.addEventListener('click', (e) => { e.preventDefault(); this.auto = cfg.auto; this.charger(); });
      pag.before(this.bloc);
      this.majBouton();
      this.observe = new IntersectionObserver((entrees) => {
        if (entrees.some((x) => x.isIntersecting) && this.auto > 0 && !this.enCours) { this.auto--; this.charger(); }
      }, { rootMargin: '0px 0px 900px 0px' });
      this.observe.observe(this.bloc);
      if (restauration?.derniere > this.derniere) this.restaurer(restauration);
      else restauration = null;
    },
    url(page) {
      const u = new URL(location.href);
      u.searchParams.set('page', page);
      return u;
    },
    majBouton() {
      if (!this.bloc) return;
      const u = this.url(this.derniere + 1);
      this.bouton.href = `${u.pathname}${u.search}`;
      this.bloc.classList.toggle('ec-more--auto', this.auto > 0);
    },
    async recuperer(page) {
      const u = this.url(page);
      if (cfg.section) u.searchParams.set('section_id', cfg.section);
      const r = await fetch(u, { credentials: 'same-origin' });
      if (!r.ok) throw new Error(r.status);
      return new DOMParser().parseFromString(await r.text(), 'text/html');
    },
    ajouter(doc) {
      const items = [...(doc.getElementById('product-grid')?.children || [])];
      const frag = document.createDocumentFragment();
      for (const li of items) {
        const lien = li.querySelector('a[href]')?.pathname;
        if (lien && this.vus.has(lien)) continue;
        if (lien) this.vus.add(lien);
        frag.append(document.adoptNode(li));
      }
      this.grid.append(frag);
      this.derniere++;
      const pag = doc.querySelector('.pagination-wrapper');
      if (pag) this.total = Math.max(this.derniere, ...[...pag.querySelectorAll('.pagination__item')].map((a) => parseInt(a.textContent, 10)).filter((n) => n > 0));
      if (!items.length) this.total = this.derniere;
    },
    debut() {
      this.enCours = true;
      this.bloc.classList.add('is-loading');
      this.bloc.querySelector('.ec-more__statut').textContent = T.chargement;
    },
    async charger() {
      if (this.enCours || this.derniere >= this.total) return false;
      this.debut();
      const grid = this.grid;
      try {
        const doc = await this.recuperer(this.derniere + 1);
        if (grid !== this.grid || !grid.isConnected) return false;          // filtres changés entre-temps
        this.ajouter(doc);
        return true;
      } catch {
        this.auto = 0;                                                       // réseau : on laisse le bouton
        return false;
      } finally {
        this.fin();
      }
    },
    fin() {
      this.enCours = false;
      if (!this.bloc) return;
      this.bloc.classList.remove('is-loading');
      this.bloc.querySelector('.ec-more__statut').textContent = '';
      if (this.derniere >= this.total) { this.observe.disconnect(); this.bloc.remove(); this.bloc = null; return; }
      this.majBouton();
      // bloc encore visible après l'ajout (grand écran) : on continue tant qu'il reste des chargements automatiques
      requestAnimationFrame(() => {
        if (!this.bloc || this.auto <= 0) return;
        const r = this.bloc.getBoundingClientRect();
        if (r.top < innerHeight + 900) { this.auto--; this.charger(); }
      });
    },
    async restaurer(etat) {
      // pages récupérées en parallèle (0,5 à 1 s chacune côté Shopify), ajoutées dans l'ordre ; 15 pages au plus
      this.auto = 0;
      const grid = this.grid;
      const pages = [];
      for (let p = this.derniere + 1; p <= Math.min(etat.derniere, this.total, this.premiere + 15); p++) pages.push(p);
      this.debut();
      try {
        const docs = await Promise.all(pages.map((p) => this.recuperer(p).catch(() => null)));
        for (const doc of docs) { if (!doc || grid !== this.grid) break; this.ajouter(doc); }
      } finally { this.fin(); }
      this.auto = etat.auto ?? cfg.auto;
      this.majBouton();
      window.scrollTo(0, etat.y || 0);
      restauration = null;
      history.scrollRestoration = 'auto';
    },
  };
  // mémoriser pages chargées et position avant d'ouvrir une fiche
  container.addEventListener('click', (e) => {
    if (!infini.grid || !e.target.closest('#product-grid a[href]')) return;
    ecrireEtat({ derniere: infini.derniere, y: scrollY, auto: infini.auto });
  });
  window.addEventListener('pagehide', () => { if (infini.grid && infini.derniere > infini.premiere) ecrireEtat({ derniere: infini.derniere, y: scrollY, auto: infini.auto }); });

  /* ------------------------------------------------------------------ recherche dans les filtres */
  // { t: [[id, nom, {langue: nom}]], c: { handle: [index] }, o: { paramètre: [ids dans l'ordre] } }, chargé à la demande
  let chargement = null;
  const donnees = () => (chargement ||= (cfg.equipes ? fetch(cfg.equipes).then((r) => r.json()) : Promise.reject(new Error('pas de données')))
    .catch(() => ({ t: [], c: {}, o: {} })));
  const handleCollection = cfg.collection || location.pathname.match(/\/collections\/([^/?#]+)/)?.[1];
  async function equipesManquantes() {
    if (!cfg.equipes || !handleCollection) return [];
    const equipes = await donnees();
    const loc = LANG.startsWith('pt') ? 'pt-PT' : LANG.slice(0, 2);
    return (equipes.c[handleCollection] || []).map((i) => equipes.t[i]).map(([id, nom, tr]) => ({ id: `gid://shopify/Metaobject/${id}`, nom: (tr && tr[loc]) || nom }));
  }
  function appliquerEquipe(id) {
    const p = new URLSearchParams(location.search);
    p.delete('page');
    if (!p.getAll(PARAM_EQUIPE).includes(id)) p.append(PARAM_EQUIPE, id);
    if (typeof FacetFiltersForm !== 'undefined') {
      document.querySelectorAll('details.js-filter[open]').forEach((d) => d.removeAttribute('open'));
      document.querySelector('menu-drawer.mobile-facets__wrapper')?.close?.();
      FacetFiltersForm.renderPage(p.toString());
    } else location.search = p.toString();
  }

  // Une équipe hors des 100 valeurs de Shopify, une fois choisie, n'a pas de pastille « Équipe : … ✕ » :
  // on l'ajoute, au même format que celles du thème (facet-remove : clic = filtre retiré sans recharger).
  async function pastillesManquantes() {
    const actifs = new URLSearchParams(location.search).getAll(PARAM_EQUIPE);
    if (!actifs.length) return;
    const connus = new Set([...document.querySelectorAll(`input[name="${PARAM_EQUIPE}"]`)].map((i) => i.value));
    const manquants = actifs.filter((id) => !connus.has(id));
    if (!manquants.length) return;
    const noms = new Map((await equipesManquantes()).map((e) => [e.id, e.nom]));
    const filtre = document.querySelector(`details.js-filter input[name="${PARAM_EQUIPE}"]`)?.closest('details');
    const libelleFiltre = filtre?.querySelector('summary span')?.firstChild?.textContent.trim() || '';
    const croix = document.querySelector('.active-facets .icon-close-small')?.outerHTML || '';
    for (const zone of document.querySelectorAll('.active-facets-desktop, .active-facets-mobile')) {
      for (const id of manquants) {
        if (!noms.has(id) || zone.querySelector(`[data-ec-pastille="${CSS.escape(id)}"]`)) continue;
        const p = new URLSearchParams(location.search);
        const reste = p.getAll(PARAM_EQUIPE).filter((v) => v !== id);
        p.delete(PARAM_EQUIPE); reste.forEach((v) => p.append(PARAM_EQUIPE, v)); p.delete('page');
        const tpl = document.createElement('template');
        tpl.innerHTML = `<facet-remove data-ec-pastille=""><a class="active-facets__button active-facets__button--light"><span class="active-facets__button-inner button button--tertiary"><span class="ec-pastille__texte"></span>${croix}</span></a></facet-remove>`;
        const el = tpl.content.firstElementChild;
        el.dataset.ecPastille = id;
        el.querySelector('a').href = `${location.pathname}${p.toString() ? `?${p}` : ''}`;
        el.querySelector('.ec-pastille__texte').textContent = `${libelleFiltre ? `${libelleFiltre}: ` : ''}${noms.get(id)}`;
        zone.prepend(el);
      }
    }
  }

  function listeDe(details) {
    return details.querySelector('ul.mobile-facets__list') || details.querySelector('ul.facets__list:not(.no-js-list)');
  }
  function libelle(li) {
    const nom = li.querySelector('.ec-nom');
    if (nom) return nom.textContent.trim();
    const span = li.querySelector('label > span[aria-hidden="true"]') || li.querySelector('label');
    return (span?.textContent || '').replace(/\(\d+\)\s*$/, '').trim();
  }

  /* Habillage DA : nom et nombre séparés, écusson / drapeau devant les équipes (mêmes images que le méga-menu),
     listes courtes en boutons, compteur jaune sur la pastille d'un filtre actif, titre en tête du menu ouvert. */
  const MOTS_VIDES = /^(fc|ac|as|cf|sc|cd|ud|rc|ca|sk|de|la|le|el|the|club)$/i;
  const initiales = (nom) => (nom.replace(/\(.*?\)/g, '').split(/[\s'-]+/).filter((m) => m && !MOTS_VIDES.test(m)).slice(0, 2).map((m) => m[0]).join('') || nom.slice(0, 2)).toUpperCase();
  const pastilleInitiales = (nom) => { const s = document.createElement('span'); s.className = 'ec-logo ec-logo--init'; s.textContent = initiales(nom); return s; };
  function urlLogo(code) {
    if (!code) return null;
    if (code.startsWith('d:')) return cfg.equipes ? cfg.equipes.replace(/elite-filtres-equipes\.json.*$/, `elite-flag-${code.slice(2)}.png`) : null;
    return `/cdn/shop/files/${encodeURIComponent(code.slice(2))}?width=64`;
  }
  async function poserLogo(li, span, nom) {
    const code = (await donnees()).l?.[li.querySelector('input').value.split('/').pop()];
    if (!span.isConnected || span.querySelector('.ec-logo')) return;
    const url = urlLogo(code);
    let el = pastilleInitiales(nom);
    if (url) {
      const img = new Image();
      img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
      img.className = `ec-logo${code.startsWith('d:') ? ' ec-logo--drapeau' : ''}`;
      img.addEventListener('error', () => img.replaceWith(pastilleInitiales(nom)), { once: true });
      img.src = url;
      el = img;
    }
    span.prepend(el);
  }
  function decorer(liste, items, estEquipe) {
    for (const li of items) {
      const span = li.querySelector('label > span[aria-hidden="true"]');
      if (!span || span.querySelector('.ec-nom')) continue;
      const m = span.textContent.trim().match(/^([\s\S]*?)\s*\((\d+)\)$/);
      const nom = document.createElement('span');
      nom.className = 'ec-nom';
      nom.textContent = m ? m[1] : span.textContent.trim();
      span.replaceChildren(nom);
      if (m) { const nb = document.createElement('span'); nb.className = 'ec-nb'; nb.textContent = m[2]; span.append(nb); }
      if (estEquipe) poserLogo(li, span, nom.textContent);
    }
    // 10 choix au plus (Saison, Type de maillot, Version, Continent, Disponibilité…) : boutons ; sinon liste
    const court = !estEquipe && items.length <= 10;
    liste.classList.toggle('ec-chips', court);
    liste.classList.toggle('ec-liste', !court);
  }
  function compteur(details, n) {
    details.classList.toggle('ec-actif', n > 0);
    const div = details.querySelector('summary > div');
    if (!div) return;
    let badge = div.querySelector('.ec-badge');
    if (!n) { badge?.remove(); return; }
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'ec-badge';
      div.insertBefore(badge, div.querySelector('.icon-caret, .mobile-facets__arrow'));
    }
    if (badge.textContent !== String(n)) badge.textContent = n;   // sinon le MutationObserver tournerait en boucle
  }
  function titrePanneau(details) {
    const entete = details.querySelector('.facets__header');
    if (!entete || entete.querySelector('.ec-ptitre')) return;
    const titre = document.createElement('p');
    titre.className = 'ec-ptitre';
    titre.textContent = details.querySelector('summary span')?.firstChild?.textContent.trim() || '';
    entete.prepend(titre);
  }
  // tiroir mobile ouvert : la barre de filtres (z-index 4, sous l'en-tête collant) passe devant l'en-tête
  // filtre ouvert dans le tiroir : sa liste du dessous remonte en haut (le filtre s'y ouvre en position absolue, il
  // apparaîtrait décalé) et ne défile plus (sur iPhone elle emmenait le filtre ouvert et son bouton retour)
  const filtreMobile = (details) => {
    const main = details.closest('.mobile-facets__main');
    if (!main) return;
    if (details.open) main.scrollTop = 0;
    main.classList.toggle('ec-filtre-ouvert', !!main.querySelector(':scope > details.mobile-facets__details[open]'));
  };
  document.addEventListener('click', (e) => {
    const s = e.target.closest?.('details.mobile-facets__details > summary');
    if (s) setTimeout(() => filtreMobile(s.parentElement), 0);
  }, true);
  document.addEventListener('toggle', (e) => {
    if (e.target.matches?.('details.mobile-facets__details')) filtreMobile(e.target);
    if (!e.target.matches?.('details.mobile-facets__disclosure')) return;
    e.target.closest('.facets-wrapper')?.classList.toggle('ec-tiroir-ouvert', e.target.open);
    document.body.classList.toggle('ec-tiroir-ouvert', e.target.open);   // masque la bulle du chat, posée sur « Appliquer »
  }, true);
  // Type de maillot, Version, Saison : valeurs remises dans l'ordre voulu (Shopify trie par nom : « 2024-25, 2025-26… »)
  const ORDONNES = /^filter\.p\.m\.custom\.(type_maillot|version|saison)$/;
  async function trier(liste, items) {
    const nom = items[0]?.querySelector('input')?.name;
    if (!nom || !ORDONNES.test(nom) || liste.dataset.ecTrie) return;
    liste.dataset.ecTrie = '1';
    const ordre = (await donnees()).o?.[nom];
    if (!ordre || !liste.isConnected) return;
    const rang = new Map(ordre.map((id, i) => [`gid://shopify/Metaobject/${id}`, i]));
    const cle = (li) => rang.get(li.querySelector('input').value) ?? 999;
    items.sort((a, b) => cle(a) - cle(b)).forEach((li) => liste.append(li));
  }
  // filtres masqués sur cette collection (métachamp custom.filtres_masques, ex. championnat sur la page Ligue 1) :
  // la grille les cache déjà en CSS (:has), ceci couvre les navigateurs sans :has
  const PARAMS_MASQUES = (cfg.masquer || []).map((k) => (k === 'prix' ? 'filter.v.price' : k === 'disponibilite' ? 'filter.v.availability' : `filter.p.m.custom.${k}`));
  function ameliorer(details) {
    if (PARAMS_MASQUES.some((p) => details.querySelector(`input[name^="${p}"]`))) { details.classList.add('ec-filtre-inutile'); return; }
    const liste = listeDe(details);
    if (!liste) {                                                    // prix : actif dès qu'un montant est saisi
      details.classList.toggle('ec-actif', [...details.querySelectorAll('input.field__input')].some((i) => i.value !== ''));
      titrePanneau(details);
      return;
    }
    const items = [...liste.children].filter((li) => li.querySelector('input[type="checkbox"]'));
    const estEquipe = items.some((li) => li.querySelector(`input[name="${PARAM_EQUIPE}"]`));
    trier(liste, items);
    decorer(liste, items, estEquipe);
    const actifs = items.filter((li) => li.querySelector('input').checked).length;
    compteur(details, actifs);
    // un seul choix possible et rien de coché : le filtre ne sert à rien ici (les choix grisés, à 0 produit une fois
    // d'autres filtres cochés, ne comptent pas)
    const possibles = items.filter((li) => !li.querySelector('input').disabled).length;
    details.classList.toggle('ec-filtre-inutile', possibles < 2 && !actifs);
    titrePanneau(details);
    if (!cfg.recherche || details.querySelector('.ec-fsearch')) return;
    if (items.length < cfg.rechercheMin && !estEquipe) return;
    if (items.length < 6 || liste.classList.contains('ec-chips')) return;
    const boite = document.createElement('div');
    boite.className = 'ec-fsearch';
    boite.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><circle cx="8.5" cy="8.5" r="6" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M13 13l5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg><input type="search" autocomplete="off" spellcheck="false" enterkeyhint="search"><p class="ec-fsearch__vide" hidden></p><ul class="ec-fsearch__extra list-unstyled" hidden></ul>';
    const champ = boite.querySelector('input');
    champ.placeholder = estEquipe ? T.equipe : T.chercher;
    champ.setAttribute('aria-label', champ.placeholder);
    const vide = boite.querySelector('.ec-fsearch__vide');
    vide.textContent = T.aucun;
    const extra = boite.querySelector('.ec-fsearch__extra');
    const connus = new Set(items.map((li) => li.querySelector('input').value));
    const filtrer = async () => {
      const q = norm(champ.value);
      let n = 0;
      for (const li of items) { const ok = !q || norm(libelle(li)).includes(q); li.classList.toggle('ec-fhide', !ok); if (ok) n++; }
      extra.replaceChildren();
      if (q && estEquipe) {
        const autres = (await equipesManquantes()).filter((e) => !connus.has(e.id) && norm(e.nom).includes(q)).slice(0, 20);
        for (const e of autres) {
          const li = document.createElement('li');
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'ec-fsearch__choix';
          b.textContent = e.nom;
          b.addEventListener('click', () => appliquerEquipe(e.id));
          li.append(b);
          extra.append(li);
          n++;
        }
      }
      extra.hidden = !extra.children.length;
      vide.hidden = n > 0;
    };
    // facets.js soumet le formulaire à chaque « input » : le champ ne doit pas déclencher de rechargement
    champ.addEventListener('input', (e) => { e.stopPropagation(); filtrer(); });
    champ.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.preventDefault(); });
    champ.addEventListener('change', (e) => e.stopPropagation());
    const entete = details.querySelector('.facets__header');
    if (entete) entete.append(boite);
    else liste.before(boite);
    details.addEventListener('toggle', () => {
      if (details.open && matchMedia('(pointer: fine)').matches && boite.isConnected) champ.focus({ preventScroll: true });
    });
  }
  const installerFiltres = () => document.querySelectorAll('facet-filters-form details.js-filter, .mobile-facets details.js-filter').forEach(ameliorer);

  /* ------------------------------------------------------------------ démarrage et suivi des rechargements */
  let attente = 0;
  const tout = () => { cancelAnimationFrame(attente); attente = requestAnimationFrame(() => { installerFiltres(); infini.installe(); pastillesManquantes(); }); };
  tout();
  new MutationObserver((mutations) => {
    if (mutations.some((m) => m.target === container)) { infini.grid = null; infini.bloc = null; infini.observe?.disconnect(); }
    tout();
  }).observe(container, { childList: true });
  document.querySelectorAll('facet-filters-form, .mobile-facets').forEach((el) => new MutationObserver(tout).observe(el, { childList: true, subtree: true }));
})();
